import { Close, Save, Cancel, Delete, Print } from "@mui/icons-material";
import {
    Avatar,
    Button,
    ButtonGroup,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Paper,
    Typography,
    TextField,
    FormControlLabel,
    Box,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

import thanq from "../api/thanq";
import QRPrint from "./QRPrint";
import ConfigData from "../utils/config";

const UserInfo = ({ user, open, setOpen, onDeleteUser, onSaveUser, companies = [], isSuperAdmin = false }) => {
    const QRComponentRef = useRef();

    const handleProfilePrint = useReactToPrint({
        contentRef: QRComponentRef,
        documentTitle: `User-${user.phone}`,
    });
    const [bankDetails, setBankDetails] = useState({});
    const [formData, setFormData] = useState({});
    const [bankFormData, setBankFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const configInstance = new ConfigData();

    const [workTypeOptions, setWorkTypeOptions] = useState(Object.keys(configInstance.options));

    useEffect(() => {
        const loadConfigOptions = async () => {
            await configInstance.loadConfig();
            setWorkTypeOptions(Object.keys(configInstance.options));
        };
        loadConfigOptions();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (user?.id && open === true) {
            getBankDetails();
            // Initialize form data when user changes
            setFormData({
                name: user.name || "",
                onboarded: user.onboarded || false,
                phoneNumber: (user.phoneNumber || "").replace("+91", ""),
                workCity: user.workCity || "",
                workPlace: user.workPlace || "",
                workType: workTypeOptions.includes(user.workType) ? user.workType : "Other",
                companyId: user.companyId 
            });
            // Initialize image preview
            setImagePreview(user?.profileImage || null);
            setProfileImage(null);
        }
        // eslint-disable-next-line
    }, [open, user?.id]);

    const getBankDetails = async () => {
        try {
            const response = await thanq.get(`/admin/user/${user.id}/bank`);
            const bankData = response?.data || {};
            setBankDetails(bankData);
            setBankFormData({
                upi: bankData?.upi || "",
            });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        }
    };

    const handleFormChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBankFormChange = (field, value) => {
        setBankFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Convert profile image to base64 if it exists
            let imageBase64 = null;
            if (profileImage) {
                imageBase64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result.toString());
                    reader.onerror = reject;
                    reader.readAsDataURL(profileImage);
                });
            }

            const saveData = {
                formData: { ...formData },
                bankFormData,
                profileImage: imageBase64,
            };

            // Sanitize and normalize phone number
            let phone = (formData.phoneNumber || "").trim();

            if (phone) {
                // Remove all non-digit characters (like spaces, hyphens)
                const digits = phone.replace(/\D/g, "");

                // Add +91 if not already present
                if (!phone.startsWith("+91")) {
                    // If user entered 10 digits (Indian format)
                    if (digits.length === 10) {
                        saveData.formData.phoneNumber = `+91${digits}`;
                    } else if (digits.startsWith("91") && digits.length === 12) {
                        saveData.formData.phoneNumber = `+${digits}`;
                    } else {
                        saveData.formData.phoneNumber = `+${digits}`; // fallback for other formats
                    }
                }
            }

            if (onSaveUser) {
                await onSaveUser(saveData);
            } else {
                enqueueSnackbar("Save functionality not implemented", { variant: "warning" });
            }
        } catch (err) {
            enqueueSnackbar(err.message || "Something went wrong", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };
    console.log("user", user);
    const handleCancel = () => {
        // Reset form data to original values
        setFormData({
            name: user.name || "",
            totalQ: user.totalQ || 0,
            onboarded: user.onboarded || false,
            phoneNumber: user.phoneNumber || "",
            workCity: user.workCity || "",
            workPlace: user.workPlace || "",
            workType: user.workType || "",
            companyId: user.companyId 
        });
        // Reset bank form data to original values
        setBankFormData({
            upi: bankDetails?.upi || "",
        });
        // Reset image data
        setImagePreview(user?.profileImage || null);
        setProfileImage(null);
        setOpen(false);
    };

    const handleDeleteUser = () => {
        if (onDeleteUser) {
            onDeleteUser(user);
        } else {
            enqueueSnackbar("Delete functionality not implemented", { variant: "warning" });
        }
    };

    const handleRemoveFromCompany = async () => {
        try {
            setLoading(true);
            const res = await thanq.delete(`/admin/user/${user.id}/company`);
            if(res?.data?.success){
                enqueueSnackbar("User removed from company", { variant: "success" });
            } else {
                enqueueSnackbar(res.data.message || "Failed to remove user from company", { variant: "error" });
            }
            // Update local form state
            handleFormChange("companyId", "");
            handleFormChange("workPlace", "");
            enqueueSnackbar("User removed from company", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.message || "Failed to remove user from company", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };
    return (
        <Drawer anchor="right" open={open} onClose={handleCancel}>
            <Paper sx={{ p: 3, width: 400 }}>
                <Grid container direction={"column"} sx={{ mb: 4 }}>
                    <Grid
                        container
                        direction={"row"}
                        sx={{
                            mb: 2,
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Edit User Info
                        </Typography>
                        <IconButton onClick={handleCancel}>
                            <Close />
                        </IconButton>
                    </Grid>
                    <Divider />

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                            <Grid item>
                                <Avatar
                                    src={imagePreview || user?.profileImage}
                                    style={{ objectFit: "cover", width: 75, height: 75 }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                            <Grid item>
                                <input
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id="profile-image-upload"
                                    type="file"
                                    onChange={handleImageChange}
                                />
                                <label htmlFor="profile-image-upload">
                                    <Button variant="outlined" component="span" size="small">
                                        Change Profile Image
                                    </Button>
                                </label>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} direction={"column"}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label="Handle ID"
                                    value={user?.handleId}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    value={formData.phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10);
                                        handleFormChange("phoneNumber", value);
                                    }}
                                    disabled={Boolean(user.onboarded)}
                                    variant="outlined"
                                    slotProps={{
                                        input: {
                                            inputMode: "numeric",
                                            pattern: "[0-9]{10}",
                                            maxLength: 10,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    +91
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="ThanQ Points"
                                    type="number"
                                    defaultValue={user.totalQ}
                                    variant="outlined"
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Display Name"
                                    value={formData.name}
                                    onChange={(e) => handleFormChange("name", e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Company"
                                    value={formData.workPlace}
                                    onChange={(e) => handleFormChange("workPlace", e.target.value)}
                                    variant="outlined"
                                    disabled={Boolean(formData.companyId)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    value={formData.workCity}
                                    onChange={(e) => handleFormChange("workCity", e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            {isSuperAdmin && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Assign Company</InputLabel>
                                        <Select
                                            value={formData.companyId || ""}
                                            label="Assign Company"
                                            onChange={(e) =>
                                                handleFormChange("companyId", e.target.value)
                                            }
                                        >
                                            <MenuItem value="">No Company</MenuItem>
                                            {companies.map((company) => (
                                                <MenuItem key={company.id} value={company.id}>
                                                    {company.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.workType || "Other"}
                                        label="Role"
                                        onChange={(e) =>
                                            handleFormChange("workType", e.target.value)
                                        }
                                    >
                                        {workTypeOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="UPI ID"
                                    value={bankFormData.upi}
                                    onChange={(e) => handleBankFormChange("upi", e.target.value)}
                                    variant="outlined"
                                    disabled={user?.tipDisabled}
                                    helperText={user?.tipDisabled ? "Tips are disabled for this user" : ""}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.onboarded}
                                            onChange={(e) =>
                                                handleFormChange("onboarded", e.target.checked)
                                            }
                                        />
                                    }
                                    label="Onboarded"
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Actions
                        </Typography>
                        <Grid container spacing={2}>
                            <ButtonGroup orientation="vertical" fullWidth>
                                <ButtonGroup orientation="horizontal" fullWidth>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleCancel}
                                        startIcon={<Cancel />}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSubmit}
                                        startIcon={<Save />}
                                        color="success"
                                        disabled={loading}
                                    >
                                        Save Changes
                                    </Button>
                                </ButtonGroup>

                                <ButtonGroup orientation="vertical" fullWidth>
                                    <Button
                                        color="info"
                                        onClick={handleProfilePrint}
                                        startIcon={<Print />}
                                        variant="contained"
                                    >
                                        Print Profile
                                    </Button>
                                    {user.companyId && isSuperAdmin && (
                                        <Button
                                            color="warning"
                                            onClick={handleRemoveFromCompany}
                                            disabled={loading}
                                        >
                                            Remove from Company
                                        </Button>
                                    )}
                                    {onDeleteUser && (
                                        <Button
                                            color="error"
                                            onClick={handleDeleteUser}
                                            startIcon={<Delete />}
                                        >
                                            Delete User
                                        </Button>
                                    )}
                                </ButtonGroup>
                            </ButtonGroup>
                        </Grid>
                    </Box>
                    <div style={{ display: "none" }}>
                        <div ref={QRComponentRef}>
                            <QRPrint
                                handleId={user?.handleId}
                                phone={formData.phoneNumber}
                                name={formData.name}
                                image={user.profileQR}
                            />
                        </div>
                    </div>
                </Grid>
            </Paper>
        </Drawer>
    );
};

export default UserInfo;
